import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message, Tag } from 'antd';
import { Button } from '@components/ui/Button';
import { format } from 'date-fns';
import { useCanCheck } from '@/ability/helpers';
import { useAuthStore } from '@stores/auth.store';
import { getRemovalRequests, approveRemovalRequest, rejectRemovalRequest } from '@services/users.service';

export const REMOVAL_REQUESTS_QUERY_KEY = ['users', 'removal-requests'];

const STATUS_TAB = { ALL: 'all', PENDING: 'pending', APPROVED: 'approved', REJECTED: 'rejected' };
const capFirst = (s) => (s && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—');

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return dateStr;
        return format(d, 'do MMMM yyyy');
    } catch {
        return dateStr;
    }
};

export const useRemovalRequests = () => {
    const queryClient = useQueryClient();
    const [statusTab, setStatusTab] = useState(STATUS_TAB.ALL);
    const [approveModalOpen, setApproveModalOpen] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [requestToApprove, setRequestToApprove] = useState(null);
    const [requestToReject, setRequestToReject] = useState(null);

    const can = useCanCheck();
    const role = useAuthStore((s) => s.role);
    const roles = useAuthStore((s) => s.roles ?? []);
    const isCompanyAdmin =
        role === 'Company Admin' || (Array.isArray(roles) && roles.includes('Company Admin'));
    const canView = can('hr.employees.view') || can('saas.users.view');
    const canApproveReject = isCompanyAdmin && can('saas.users.delete');

    const queryStatus = statusTab === STATUS_TAB.ALL ? undefined : statusTab;

    const { data: response, isError: requestsError } = useQuery({
        queryKey: [...REMOVAL_REQUESTS_QUERY_KEY, { page: 1, limit: 100, status: queryStatus }],
        queryFn: () => getRemovalRequests({ page: 1, limit: 100, status: queryStatus }),
        enabled: !!canView,
        retry: false,
        staleTime: 0,
        refetchOnMount: true,
    });

    const requests = Array.isArray(response?.data) ? response.data : [];
    const total = response?.total ?? 0;
    const pendingCount = useMemo(
        () => requests.filter((r) => (r.status || '').toLowerCase() === 'pending').length,
        [requests],
    );

    const approveMutation = useMutation({
        mutationFn: (id) => approveRemovalRequest(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: REMOVAL_REQUESTS_QUERY_KEY });
        },
    });

    const rejectMutation = useMutation({
        mutationFn: (id) => rejectRemovalRequest(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: REMOVAL_REQUESTS_QUERY_KEY });
        },
    });

    const tableData = useMemo(
        () =>
            requests.map((r) => ({
                key: r.id,
                id: r.id,
                userName: r.user?.name ?? r.userName ?? '—',
                userEmail: r.user?.email ?? r.userEmail ?? '—',
                userId: r.userId ?? r.user?.id,
                requestedBy:
                    r.requestedByUser?.name ?? r.requestedByName ?? r.requestedByUser?.email ?? r.requestedBy ?? '—',
                status: r.status,
                createdAt: r.createdAt,
                approvedBy: r.approvedBy,
                approvedAt: r.approvedAt,
                rejectedBy: r.rejectedBy,
                rejectedAt: r.rejectedAt,
            })),
        [requests],
    );

    const openApproveModal = useCallback((record) => {
        setRequestToApprove(record);
        setApproveModalOpen(true);
    }, []);

    const closeApproveModal = useCallback(() => {
        setApproveModalOpen(false);
        setRequestToApprove(null);
    }, []);

    const confirmApprove = useCallback(() => {
        if (!requestToApprove?.id) return;
        approveMutation.mutate(requestToApprove.id, {
            onSuccess: () => {
                message.success('Removal request approved. Employee has been removed from the company.');
                closeApproveModal();
            },
            onError: (err) => {
                message.error(err?.response?.data?.message || 'Failed to approve.');
            },
        });
    }, [requestToApprove, approveMutation, closeApproveModal]);

    const openRejectModal = useCallback((record) => {
        setRequestToReject(record);
        setRejectModalOpen(true);
    }, []);

    const closeRejectModal = useCallback(() => {
        setRejectModalOpen(false);
        setRequestToReject(null);
    }, []);

    const confirmReject = useCallback(() => {
        if (!requestToReject?.id) return;
        rejectMutation.mutate(requestToReject.id, {
            onSuccess: () => {
                message.success('Removal request rejected.');
                closeRejectModal();
            },
            onError: (err) => {
                message.error(err?.response?.data?.message || 'Failed to reject.');
            },
        });
    }, [requestToReject, rejectMutation, closeRejectModal]);

    const columns = useMemo(() => {
        const base = [
            { title: 'Employee', dataIndex: 'userName', key: 'userName' },
            { title: 'Email', dataIndex: 'userEmail', key: 'userEmail' },
            { title: 'Requested by', dataIndex: 'requestedBy', key: 'requestedBy' },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (v) => {
                    const raw = (v || '').toLowerCase();
                    const label = capFirst(raw);
                    let color = 'default';
                    if (raw === 'pending') color = 'orange';
                    else if (raw === 'approved') color = 'green';
                    else if (raw === 'rejected') color = 'red';
                    return <Tag color={color}>{label}</Tag>;
                },
            },
            { title: 'Requested at', dataIndex: 'createdAt', key: 'createdAt', width: 160, render: formatDate },
        ];
        if (canApproveReject) {
            base.push({
                title: 'Action',
                key: 'action',
                width: 180,
                render: (_, record) => {
                    const isPending = (record.status || '').toLowerCase() === 'pending';
                    if (!isPending) return '—';
                    return (
                        <span onClick={(e) => e.stopPropagation()}>
                            <Button
                                $primary
                                $blackOnLight
                                $size="16px"
                                $padding="8px 16px"
                                onClick={() => openApproveModal(record)}
                                style={{ marginRight: 8 }}
                            >
                                Approve
                            </Button>
                            <Button style={{ backgroundColor: 'red', border: 'none' }} $size="16px" $padding="8px 16px" onClick={() => openRejectModal(record)}>
                                Reject
                            </Button>
                        </span>
                    );
                },
            });
        }
        return base;
    }, [canApproveReject, openApproveModal, openRejectModal]);

    return {
        requests,
        total,
        pendingCount,
        tableData,
        columns,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        canView,
        canApproveReject,
        requestsError,
        approveModalOpen,
        closeApproveModal,
        requestToApprove,
        confirmApprove,
        approvePending: approveMutation.isPending,
        rejectModalOpen,
        closeRejectModal,
        requestToReject,
        confirmReject,
        rejectPending: rejectMutation.isPending,
        openApproveModal,
        openRejectModal,
        formatDate,
        capFirst,
    };
};
